import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { localeFromLng } from "@/v2/lib/formatDate";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useBiens } from "@/hooks/useBiens";
import { useProfiles } from "@/hooks/useProfiles";
import { useRole } from "@/contexts/RoleContext";
import { isForSale } from "@/lib/transaction";
import { DataTable, type ColumnDef, type SortState } from "@/v2/components/DataTable";
import type { BienRow } from "@/schemas/bien";
import { Building2, ChevronLeft, ChevronRight, AlertTriangle, Search, Columns3, FileSpreadsheet, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";
import { getLifecycleStage, getPublicationStage, publicationSortValue, lifecycleSortValue, isCycleIncomplet } from "@/v2/lib/bienLifecycle";
import { getMarketBucket, getMailingCompleteness, isPebAbsent, isPebExpiring6m, type MarketBucket } from "@/v2/lib/dashboardBuckets";
import { pebValidity } from "@/lib/pebCycle";
import { setListOrder } from "@/hooks/useListNavigation";
// Sélecteur de colonnes + export XLS (registry dédié Catalogue).
import { ColumnPickerModal } from "@/components/biens/ColumnPickerModal";
import { CATALOGUE_COLUMNS, type CatalogueColumnKey } from "@/v2/lib/catalogueColumns";
import { getOrderedVisibleColumns, defaultVisibleKeys, sanitizeVisibleKeys } from "@/lib/columnRegistry";
import { dispoCell } from "@/lib/installationDisplay";
import { disponibiliteLabel } from "@/v2/lib/disponibiliteLabel";
import { typeBienLabel } from "@/lib/immoweb/estateType";
import { PvtBadge, quality3 } from "@/v2/components/PvtBadge";
// Filtres de sélection partagés avec Apparts for Rent (parité).
import { CheckDropdown, Toggle, PostalCodeFilter } from "@/components/apparts/FilterControls";

// Clé localStorage de la sélection de colonnes Catalogue (par navigateur/user).
const CATALOGUE_COLS_LS_KEY = "skyline-catalogue-columns-v1";
// Clé localStorage du tri Catalogue — persiste au retour depuis une fiche.
const CATALOGUE_SORT_LS_KEY = "skyline-catalogue-sort-v1";
// Clés localStorage des filtres de sélection (meublé, company, région,
// chambres, codes postaux) — même besoin que le tri : survivre au retour
// depuis une fiche, pas seulement au sein de la session courante.
const CATALOGUE_MEUBLE_LS_KEY = "skyline-catalogue-meuble-v1";
const CATALOGUE_COMPANY_LS_KEY = "skyline-catalogue-company-v1";
const CATALOGUE_REGION_LS_KEY = "skyline-catalogue-region-v1";
const CATALOGUE_CHAMBRE_LS_KEY = "skyline-catalogue-chambre-v1";
const CATALOGUE_POSTAL_LS_KEY = "skyline-catalogue-postal-v1";
// Chip de statut (Tous/Loué/Disponible/...) — même besoin : survivre au
// retour depuis une fiche (signalé user 2026-08-19).
const CATALOGUE_STATUS_LS_KEY = "skyline-catalogue-status-v1";

function readStoredSort(key: string): SortState | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.key === "string" && (parsed.dir === "asc" || parsed.dir === "desc")) {
      return parsed as SortState;
    }
    return null;
  } catch {
    return null;
  }
}

function readStoredValue<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeStoredValue(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* quota / privacy mode → ignore */
  }
}

const formatDate = (s: string | null | undefined, lng: string) => {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString(localeFromLng(lng), { day: "2-digit", month: "2-digit", year: "numeric" });
};

/** Nombre de jours entre seraDispoPour et today. Null si pas de date ou
 *  si la date est dans le futur (le bien n'est pas encore sur le marché). */
const daysSinceDispo = (s?: string | null): number | null => {
  if (!s) return null;
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  const ms = Date.now() - d.getTime();
  if (ms < 0) return null; // dispo future
  return Math.floor(ms / (24 * 60 * 60 * 1000));
};

/** Affichage "Libre depuis" : "—" si pas de date ou dispo future,
 *  "Xj" si <60 jours, sinon "X mois" arrondis. Couleur rouge si ≥6 mois. */
const renderLibreDepuis = (s?: string | null) => {
  const days = daysSinceDispo(s);
  if (days === null) return <span className="text-text-muted">—</span>;
  const label = days < 60 ? `${days} j` : `${Math.floor(days / 30)} mois`;
  const color = days >= 180 ? "text-danger font-semibold" : days >= 90 ? "text-warning" : "text-text-secondary";
  return <span className={color}>{label}</span>;
};

const dispoChip = (dispo: string | undefined, t: (k: string) => string) => {
  if (!dispo) return null;
  const upper = dispo.toUpperCase();
  // EN PRÉPARATION (legacy) traité comme DISPONIBLE — la V2 ne distingue plus.
  const style =
    upper === "DISPONIBLE" || upper === "EN PRÉPARATION"
      ? "bg-success/15 text-success"
      : upper === "LOUÉ"
        ? "bg-accent/15 text-accent"
        : upper === "VENDU"
          ? "bg-gray-100 text-text-muted"
          : "bg-gray-100 text-text-muted";
  const label = disponibiliteLabel(dispo, t);
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${style}`}>
      {label}
    </span>
  );
};

// Bordure épaisse à 3 couleurs (vert/orange/rouge) en plus du fond, pour
// distinguer "expire dans <6 mois" (à refaire bientôt) de "expiré/absent" (à
// refaire d'urgence) — le même rouge pour les 2 cas ne le disait pas
// (signalé Thibaud 2026-08-20 : "il affiche le certificat précédent [encore]
// même si plus valide"). Fond conservé (vert/rouge) pour ne pas rendre
// "expiring" méconnaissable dans le reste de l'app.
const PEB_VALIDITY_STYLES: Record<ReturnType<typeof pebValidity>, string> = {
  valid: "bg-success/15 text-success border-2 border-success",
  expiring: "bg-warning/15 text-warning border-2 border-warning",
  expired: "bg-danger/15 text-danger border-2 border-danger",
};

const pebChip = (b: BienRow) => {
  if (!b.classePeb) return <span className="text-danger">—</span>;
  const validity = pebValidity(b.pebFinLe, b.classePeb);
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${PEB_VALIDITY_STYLES[validity]}`}>
      {b.classePeb}
    </span>
  );
};

type StatusFilter =
  | "all"
  | "blocked"
  | "cycle-incomplet"
  | "dispo"
  | "loue"
  | "vendu"
  | "incomplete-mailing"
  | "sous-option"
  | "peb-absent"
  | "peb-expirant"
  | `bucket-${MarketBucket}`;

const PAGE_SIZE = 50;

const STATUS_FILTERS: { key: StatusFilter; labelKey: string; matches: (b: BienRow) => boolean }[] = [
  // "Tous" = vraiment tous les biens, vendus compris (demande Thibaud
  // 2026-10-20, revient sur l'exclusion des vendus du 2026-08-19 / #94).
  { key: "all", labelKey: "common.all", matches: () => true },
  {
    key: "blocked",
    labelKey: "v2.catalogue.chips.blocked",
    matches: (b) => getLifecycleStage(b).stage === "draft_clevel",
  },
  // Cycle de sortie ouvert où il manque quelqu'un : collaborateur (bloquant)
  // OU prestataire ELS / expert PEB d'un process lancé (non bloquants —
  // décision Thibaud 2026-09-15 soir). Second garde-fou, distinct de l'identité.
  {
    key: "cycle-incomplet",
    labelKey: "v2.catalogue.chips.cycleIncomplet",
    matches: (b) => isCycleIncomplet(b),
  },
  {
    key: "incomplete-mailing",
    labelKey: "v2.catalogue.chips.incomplete",
    matches: (b) => {
      const d = (b.disponibilite || "").toUpperCase();
      if (d === "LOUÉ" || d === "VENDU") return false;
      if (getLifecycleStage(b).stage === "draft_clevel") return false;
      return !getMailingCompleteness(b).complete;
    },
  },
  {
    key: "sous-option",
    labelKey: "v2.catalogue.chips.sousOption",
    matches: (b) => !!b.sousOption,
  },
  { key: "dispo", labelKey: "v2.catalogue.chips.dispo", matches: (b) => {
    const d = (b.disponibilite || "").toUpperCase();
    return d === "DISPONIBLE" || d === "EN PRÉPARATION";
  } },
  { key: "loue", labelKey: "v2.catalogue.chips.loue", matches: (b) => (b.disponibilite || "").toUpperCase() === "LOUÉ" },
  { key: "vendu", labelKey: "v2.catalogue.chips.vendu", matches: (b) => (b.disponibilite || "").toUpperCase() === "VENDU" },
];

/** Filtres invisibles (pas dans les chips) — utilisés via URL ?filter=…
 *  (bucket-… depuis les tuiles dispo, peb-absent / peb-expirant depuis les
 *  tuiles PEB du dashboard CLevel). Mêmes prédicats que les compteurs des
 *  tuiles → la liste filtrée correspond exactement au chiffre cliqué. */
function matchFilter(b: BienRow, filter: StatusFilter): boolean {
  if (filter.startsWith("bucket-")) {
    const target = filter.replace("bucket-", "") as MarketBucket;
    return getMarketBucket(b) === target;
  }
  if (filter === "peb-absent") return isPebAbsent(b);
  if (filter === "peb-expirant") return isPebExpiring6m(b);
  const f = STATUS_FILTERS.find((s) => s.key === filter);
  return f ? f.matches(b) : true;
}

// Options des filtres de sélection (alignées sur Apparts for Rent).
const REGION_OPTIONS = ["bruxelles", "flandre", "wallonie"];
const CHAMBRE_OPTIONS = ["1", "2", "3", "4+"];
const REGION_LABEL_KEY: Record<string, string> = {
  bruxelles: "regions.bruxelles",
  flandre: "regions.flandre",
  wallonie: "regions.wallonie",
};

export function BiensCataloguePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { role } = useRole();
  const { data: allBiens, isLoading } = useBiens();
  // Catalogue LOCATION : les biens en vente vivent dans le catalogue dédié
  // (/v2/biens-vente, chantier Catalogue Sales 2026-07-12). Filtre en
  // `!isForSale` (et non `isForRent`) pour que les biens à transaction NULL
  // (historiques) restent visibles ici, comme avant.
  const biens = useMemo(
    () => (allBiens || []).filter((b) => !isForSale(b.transaction)),
    [allBiens],
  );
  const { data: profiles = [] } = useProfiles();
  const [searchParams] = useSearchParams();
  // Défaut C-Level : on arrive directement sur "Disponibles" (sa vue de travail).
  // Les autres rôles gardent "Tous". Un ?filter= dans l'URL (ex. depuis le
  // Pipeline → "À débloquer") reste prioritaire.
  const defaultStatusFilter: StatusFilter = role === "c_level" ? "dispo" : "all";
  const initialFilter =
    (searchParams.get("filter") as StatusFilter) ||
    readStoredValue<StatusFilter | null>(CATALOGUE_STATUS_LS_KEY, null) ||
    defaultStatusFilter;
  const [status, setStatusState] = useState<StatusFilter>(initialFilter);
  const setStatus = (s: StatusFilter) => {
    setStatusState(s);
    writeStoredValue(CATALOGUE_STATUS_LS_KEY, s);
  };
  const [page, setPage] = useState(0);

  // Sync URL → state quand on arrive avec ?filter=blocked (depuis Pipeline).
  // Ne se déclenche QUE si l'URL porte explicitement un ?filter= — sinon ça
  // écraserait systématiquement le choix restauré depuis localStorage au
  // retour d'une fiche (bug signalé user 2026-08-19 : le chip retombait sur
  // "Disponible" au lieu de garder "Loué").
  useEffect(() => {
    const urlFilter = searchParams.get("filter") as StatusFilter | null;
    if (urlFilter && urlFilter !== status) setStatus(urlFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: 0, blocked: 0, "cycle-incomplet": 0, dispo: 0, loue: 0, vendu: 0, "incomplete-mailing": 0, "sous-option": 0 };
    const list = biens || [];
    for (const b of list) {
      const d = (b.disponibilite || "").toUpperCase();
      c.all++;
      if (d === "DISPONIBLE" || d === "EN PRÉPARATION") c.dispo++;
      else if (d === "LOUÉ") c.loue++;
      else if (d === "VENDU") c.vendu++;
      if (b.sousOption) c["sous-option"]++;
      const ls = getLifecycleStage(b);
      if (ls.stage === "draft_clevel") c.blocked++;
      else {
        // Un cycle incomplet n'empêche pas la mise en marché : le bien compte
        // aussi dans "À compléter (mailing)" s'il y manque des champs.
        if (isCycleIncomplet(b)) c["cycle-incomplet"]++;
        if (d !== "LOUÉ" && d !== "VENDU" && !getMailingCompleteness(b).complete) {
          c["incomplete-mailing"]++;
        }
      }
    }
    return c;
  }, [biens]);

  // Recherche libre (référence, adresse, projet, commune). Appliquée AVANT
  // pagination pour chercher sur tous les biens et pas seulement la page
  // courante (bug user 2026-05-26).
  const [search, setSearch] = useState("");

  // Filtres de sélection (parité Apparts for Rent) : meublé, company, région,
  // chambres, codes postaux. Persistance localStorage (par navigateur) →
  // survit au retour depuis une fiche, comme le tri et les colonnes.
  const [meuble, setMeubleState] = useState(() => readStoredValue(CATALOGUE_MEUBLE_LS_KEY, false));
  const [company, setCompanyState] = useState(() => readStoredValue(CATALOGUE_COMPANY_LS_KEY, false));
  const [regionFilter, setRegionFilterState] = useState<string[]>(() => readStoredValue(CATALOGUE_REGION_LS_KEY, REGION_OPTIONS));
  const [chambreFilter, setChambreFilterState] = useState<string[]>(() => readStoredValue(CATALOGUE_CHAMBRE_LS_KEY, CHAMBRE_OPTIONS));
  const [postalCodes, setPostalCodesState] = useState<string[]>(() => readStoredValue(CATALOGUE_POSTAL_LS_KEY, [] as string[]));
  const setMeuble = (v: boolean) => { setMeubleState(v); writeStoredValue(CATALOGUE_MEUBLE_LS_KEY, v); };
  const setCompany = (v: boolean) => { setCompanyState(v); writeStoredValue(CATALOGUE_COMPANY_LS_KEY, v); };
  const setRegionFilter = (v: string[]) => { setRegionFilterState(v); writeStoredValue(CATALOGUE_REGION_LS_KEY, v); };
  const setChambreFilter = (v: string[]) => { setChambreFilterState(v); writeStoredValue(CATALOGUE_CHAMBRE_LS_KEY, v); };
  const setPostalCodes = (v: string[]) => { setPostalCodesState(v); writeStoredValue(CATALOGUE_POSTAL_LS_KEY, v); };

  // Tri contrôlé : géré par le parent pour appliquer le sort AVANT la
  // pagination (sinon le DataTable ne trie que les 50 lignes visibles).
  // Persistance localStorage (par navigateur) → survit au retour depuis une fiche.
  const [sort, setSort] = useState<SortState | null>(() => readStoredSort(CATALOGUE_SORT_LS_KEY));
  const saveSort = (next: SortState | null) => {
    setSort(next);
    try {
      if (next) localStorage.setItem(CATALOGUE_SORT_LS_KEY, JSON.stringify(next));
      else localStorage.removeItem(CATALOGUE_SORT_LS_KEY);
    } catch {
      /* quota / privacy mode → ignore */
    }
  };

  // Colonnes configurables (picker C-Level + admin) + export XLS.
  // Persistance localStorage (par navigateur) → persiste à la navigation,
  // sans dépendre du RLS admin-only de page_columns_config.
  const canConfigureColumns = role === "c_level" || role === "admin";
  const [visibleColumns, setVisibleColumns] = useState<CatalogueColumnKey[]>(() => {
    try {
      const raw = localStorage.getItem(CATALOGUE_COLS_LS_KEY);
      const sane = raw ? sanitizeVisibleKeys(CATALOGUE_COLUMNS, JSON.parse(raw)) : null;
      return sane && sane.length ? sane : defaultVisibleKeys(CATALOGUE_COLUMNS);
    } catch {
      return defaultVisibleKeys(CATALOGUE_COLUMNS);
    }
  });
  const saveColumns = (next: CatalogueColumnKey[]) => {
    const clean = sanitizeVisibleKeys(CATALOGUE_COLUMNS, next) ?? defaultVisibleKeys(CATALOGUE_COLUMNS);
    setVisibleColumns(clean);
    try {
      localStorage.setItem(CATALOGUE_COLS_LS_KEY, JSON.stringify(clean));
    } catch {
      /* quota / privacy mode → ignore */
    }
  };
  const orderedColumns = useMemo(
    () => getOrderedVisibleColumns(CATALOGUE_COLUMNS, visibleColumns),
    [visibleColumns],
  );
  const [columnPickerOpen, setColumnPickerOpen] = useState(false);
  useEffect(() => {
    if (!canConfigureColumns) setColumnPickerOpen(false);
  }, [canConfigureColumns]);
  const teamMap = useMemo(() => {
    const m: Record<string, string> = {};
    profiles.forEach((p) => {
      m[p.id] = `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || p.email;
    });
    return m;
  }, [profiles]);

  const filtered = useMemo(() => {
    let list = biens || [];
    // Le chip "all" a son propre prédicat (exclut les vendus, cf.
    // STATUS_FILTERS) : le court-circuiter pour "all" laissait passer la
    // liste NON filtrée (tous les biens, vendus compris) alors que le
    // compteur du chip, lui, les excluait déjà — d'où "Tous (20)" affiché
    // au-dessus d'un tableau montrant les ~1573 biens vendus (signalé
    // Thibaud 2026-08-19).
    list = list.filter((b) => matchFilter(b, status));
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((b) =>
        [b.referenceInterne, b.reference, b.id, b.adresse, b.ville, b.projet, typeBienLabel(b.typeBien, t), b.codeImmoweb, b.referenceExterne]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q),
      );
    }
    // Filtres de sélection (mêmes prédicats qu'Apparts for Rent).
    const regionSet = new Set(regionFilter);
    const chambreSet = new Set(chambreFilter);
    list = list.filter((b) => {
      const matchMeuble = !meuble || !!b.meuble;
      const matchCompany = !company || !!b.companyContract;
      // "Tout sélectionné" (défaut) ou "rien" = pas de filtre → on garde aussi
      // les biens sans région / sans chambres renseignées. Sinon le Catalogue
      // masquerait silencieusement des biens que la puce "Tous" compte (ce cas
      // ne se voit pas sur Apparts for Rent car ses biens sont 100 % complets).
      const matchRegion =
        regionSet.size === 0 ||
        regionSet.size === REGION_OPTIONS.length ||
        regionSet.has((b.region || "").toLowerCase());
      const nbc = Number(b.nbChambres) || 0;
      const matchChambre =
        chambreSet.size === 0 ||
        chambreSet.size === CHAMBRE_OPTIONS.length ||
        chambreSet.has(nbc >= 4 ? "4+" : String(nbc));
      const matchPostal = postalCodes.length === 0 || postalCodes.includes(b.codePostal || "");
      return matchMeuble && matchCompany && matchRegion && matchChambre && matchPostal;
    });
    return list;
  }, [biens, status, search, meuble, company, regionFilter, chambreFilter, postalCodes, t]);

  // ── Rendu / texte / tri d'une cellule selon la clé de colonne (registry) ──
  const LIFE_COLORS: Record<string, string> = {
    danger: "bg-danger/15 text-danger",
    warning: "bg-warning/15 text-warning",
    accent: "bg-accent/15 text-accent",
    success: "bg-success/15 text-success",
    muted: "bg-gray-100 text-text-muted",
  };
  const num = (v?: string | null) => parseFloat(v || "0") || 0;

  // Valeur texte (tri lexical, recherche, export XLS).
  const cellText = (key: CatalogueColumnKey, b: BienRow): string => {
    switch (key) {
      case "ref": return b.referenceInterne || b.reference || "";
      case "projet": return b.projet || "";
      case "adresse": return [b.adresse, [b.codePostal, b.ville].filter(Boolean).join(" ")].filter(Boolean).join(" — ");
      case "dispo": return b.disponibilite || "";
      case "lifecycle": return t(getLifecycleStage(b).labelKey);
      case "publication": return t(getPublicationStage(b).labelKey);
      case "libre": return b.seraDispoPour ? formatDate(b.seraDispoPour, i18n.language) : "";
      case "libreDepuis": { const d = daysSinceDispo(b.seraDispoPour); return d === null ? "" : d < 60 ? `${d} j` : `${Math.floor(d / 30)} mois`; }
      case "loyer": return b.loyerMensuel ? `${b.loyerMensuel} €` : "";
      case "surface": return b.surfaceHabitable ? `${b.surfaceHabitable} m²` : "";
      case "peb": return b.classePeb || "";
      case "commune": return b.ville || "";
      case "codePostal": return b.codePostal || "";
      case "region": return b.region || "";
      case "chambres": return b.nbChambres || "";
      case "nbSdb": return b.nbSdb || "";
      case "etage": return b.etage ?? "";
      case "charges": return b.chargesMensuelles ? `${b.chargesMensuelles} €` : "";
      case "collaborateur": return (b.collaborateurSrs && teamMap[b.collaborateurSrs]) || "";
      case "renon": return b.renonRecuLe ? formatDate(b.renonRecuLe, i18n.language) : "";
      case "finContrat": return b.finDeContrat ? formatDate(b.finDeContrat, i18n.language) : "";
      case "pebFin": return b.pebFinLe ? formatDate(b.pebFinLe, i18n.language) : "";
      case "typeBien": return typeBienLabel(b.typeBien, t);
      case "meuble": return b.meuble === true ? t("common.yes") : b.meuble === false ? t("common.no") : "";
      case "companyContract": return b.companyContract === true ? t("common.yes") : b.companyContract === false ? t("common.no") : "";
      case "cave": return dispoCell(b.caveDisponible, b.caveNumero, b.caveLoyerMensuel);
      case "parking": return dispoCell(b.parkingDisponible, b.parkingNumero, b.parkingLoyerMensuel);
      case "garage": return dispoCell(b.garageDisponible, b.garageNumero, b.garageLoyerMensuel);
      case "pvt": return `P:${quality3(b.photosOk)} V:${quality3(b.videoOk)} T:${b.textesOk === true ? "ok" : "ko"}`;
      default: return "";
    }
  };

  // Valeur de tri (number / date / string).
  const sortVal = (key: CatalogueColumnKey, b: BienRow): string | number | Date | null => {
    switch (key) {
      // Ordre du cycle, pas l'ordre alphabétique des clés internes.
      case "lifecycle": return lifecycleSortValue(getLifecycleStage(b).stage);
      case "publication": return publicationSortValue(getPublicationStage(b).stage);
      case "libre": return b.seraDispoPour ? new Date(b.seraDispoPour) : null;
      case "libreDepuis": return daysSinceDispo(b.seraDispoPour);
      case "loyer": return num(b.loyerMensuel);
      case "surface": return num(b.surfaceHabitable);
      case "charges": return num(b.chargesMensuelles);
      case "chambres": return num(b.nbChambres);
      case "nbSdb": return num(b.nbSdb);
      case "etage": return num(b.etage);
      case "renon": return b.renonRecuLe ? new Date(b.renonRecuLe) : null;
      case "finContrat": return b.finDeContrat ? new Date(b.finDeContrat) : null;
      case "pebFin": return b.pebFinLe ? new Date(b.pebFinLe) : null;
      default: return cellText(key, b);
    }
  };

  // Rendu JSX (chips/couleurs) ; fallback texte sinon.
  const renderCell = (key: CatalogueColumnKey, b: BienRow): React.ReactNode => {
    switch (key) {
      case "ref": return <span className="font-medium text-text-primary">{b.referenceInterne || b.reference || "—"}</span>;
      case "projet": return <span className="truncate block">{b.projet || "—"}</span>;
      case "adresse":
        return (
          <div className="min-w-0">
            <div className="text-text-primary truncate">{b.adresse || "—"}</div>
            <div className="text-xs text-text-muted truncate">{[b.codePostal, b.ville].filter(Boolean).join(" ")}</div>
          </div>
        );
      case "dispo": return dispoChip(b.disponibilite, t);
      case "lifecycle": {
        const ls = getLifecycleStage(b);
        return (
          <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider", LIFE_COLORS[ls.color])}>
            {ls.stage === "draft_clevel" && <AlertTriangle size={10} />}
            {t(ls.labelKey)}
          </span>
        );
      }
      case "publication": {
        const ps = getPublicationStage(b);
        return (
          <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider", LIFE_COLORS[ps.color])}>
            {t(ps.labelKey)}
          </span>
        );
      }
      case "libre": return formatDate(b.seraDispoPour, i18n.language);
      case "libreDepuis": return renderLibreDepuis(b.seraDispoPour);
      case "peb": return pebChip(b);
      case "collaborateur": return (b.collaborateurSrs && teamMap[b.collaborateurSrs]) || "—";
      case "pvt": return <PvtBadge bien={b} />;
      default: return cellText(key, b) || "—";
    }
  };

  // Colonnes du tableau, pilotées par le registry + sélection user (picker).
  // Largeur FIXE par colonne (className w-[Npx]) → la table déborde (scroll
  // horizontal) au lieu de rétrécir les colonnes.
  const columns: ColumnDef<BienRow>[] = orderedColumns.map((col) => ({
    key: col.key,
    label: t(col.i18nLabel),
    align: col.align,
    width: col.width,
    sortValue: (b) => sortVal(col.key, b),
    render: (b) => renderCell(col.key, b),
  }));
  const tableMinWidth = orderedColumns.reduce((sum, c) => sum + (c.width ?? 120), 0);

  // Tri appliqué sur la liste filtrée COMPLÈTE (avant pagination), pour
  // que le sort balaye les 1557 biens et pas juste la page courante.
  const sortedFiltered = useMemo(() => {
    if (!sort) return filtered;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortValue) return filtered;
    const cmp = (a: BienRow, b: BienRow) => {
      const va = col.sortValue!(a);
      const vb = col.sortValue!(b);
      const empty = (v: typeof va) => v === null || v === undefined || v === "";
      if (empty(va) && empty(vb)) return 0;
      if (empty(va)) return 1;
      if (empty(vb)) return -1;
      if (va instanceof Date && vb instanceof Date) return va.getTime() - vb.getTime();
      if (typeof va === "number" && typeof vb === "number") return va - vb;
      return String(va).localeCompare(String(vb), "fr", { numeric: true });
    };
    const copy = [...filtered];
    copy.sort((a, b) => (sort.dir === "asc" ? cmp(a, b) : cmp(b, a)));
    return copy;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, sort]);

  // Reset page quand filtre / recherche / tri change
  useEffect(() => { setPage(0); }, [search, status, sort, meuble, company, regionFilter, chambreFilter, postalCodes]);

  // Persiste l'ordre filtré + trié pour que la nav prev/next dans la
  // fiche V1 suive la même séquence que ce que l'user voit.
  useEffect(() => {
    setListOrder("biens", sortedFiltered.map((b) => b.id));
  }, [sortedFiltered]);

  const pageCount = Math.max(1, Math.ceil(sortedFiltered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const rows = sortedFiltered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  // Export XLS : colonnes visibles (registry) × sélection filtrée+triée complète.
  const handleExportXls = async () => {
    const ExcelJS = (await import("exceljs")).default;
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Catalogue");
    ws.columns = orderedColumns.map((col) => ({ header: t(col.i18nLabel), key: col.key, width: 20 }));
    sortedFiltered.forEach((b) => {
      const rowObj: Record<string, string> = {};
      orderedColumns.forEach((col) => { rowObj[col.key] = cellText(col.key, b); });
      ws.addRow(rowObj);
    });
    ws.getRow(1).font = { bold: true };
    ws.views = [{ state: "frozen", ySplit: 1 }];
    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = URL.createObjectURL(blob);
    const now = new Date();
    const stamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    const a = document.createElement("a");
    a.href = url;
    a.download = `catalogue-${stamp}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleStatusChange = (s: StatusFilter) => {
    setStatus(s);
    setPage(0);
  };

  const resetSelectionFilters = () => {
    setMeuble(false);
    setCompany(false);
    setRegionFilter(REGION_OPTIONS);
    setChambreFilter(CHAMBRE_OPTIONS);
    setPostalCodes([]);
  };

  const startIdx = safePage * PAGE_SIZE + 1;
  const endIdx = Math.min(startIdx + rows.length - 1, sortedFiltered.length);

  return (
    <div className="p-4 md:p-6">
      <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-text-primary flex items-center gap-2">
            <Building2 size={22} className="text-accent" />
            {t("v2.nav.catalogueLocation")}
          </h1>
          <p className="text-sm text-text-muted mt-1">
            {t("v2.catalogue.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canConfigureColumns && (
            <button
              type="button"
              onClick={() => setColumnPickerOpen(true)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-text-secondary border border-dashed border-border rounded-md hover:border-accent hover:text-accent bg-surface transition-colors cursor-pointer whitespace-nowrap"
              title={t("biens.columnPicker.title")}
            >
              <Columns3 size={14} />
              <span className="hidden sm:inline">{t("biens.columnPicker.button")}</span>
            </button>
          )}
          <button
            type="button"
            onClick={handleExportXls}
            disabled={sortedFiltered.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-text-secondary border border-dashed border-border rounded-md hover:border-accent hover:text-accent bg-surface transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            title={t("v2.catalogue.exportXlsTitle")}
          >
            <FileSpreadsheet size={14} />
            <span className="hidden sm:inline">{t("v2.catalogue.exportXls")}</span>
          </button>
        </div>
      </div>

      {/* Chips filtre statut */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {STATUS_FILTERS.map((f) => {
          const isActive = status === f.key;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => handleStatusChange(f.key)}
              className={cn(
                "px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer transition-colors border",
                isActive
                  ? "bg-accent-light text-accent border-accent"
                  : "bg-surface text-text-secondary border-border hover:border-accent/30",
              )}
            >
              <span>{t(f.labelKey)}</span>
              <span
                className={cn(
                  "ml-1.5",
                  f.key === "blocked"
                    ? "text-danger"
                    : f.key === "incomplete-mailing" || f.key === "sous-option"
                      ? "text-warning"
                      : "text-text-muted",
                )}
              >
                ({counts[f.key]})
              </span>
            </button>
          );
        })}
      </div>

      {/* Filtres de sélection (parité Apparts for Rent) — insérés entre les
          chips statut et la recherche. */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <CheckDropdown
          label={t("v2.catalogue.chambresLabel")}
          options={CHAMBRE_OPTIONS.map((v) => ({ value: v, label: t("v2.catalogue.chambreOption", { n: v }) }))}
          selected={new Set(chambreFilter)}
          onChange={(s) => setChambreFilter(Array.from(s))}
        />
        <CheckDropdown
          label={t("common.region")}
          options={REGION_OPTIONS.map((v) => ({ value: v, label: t(REGION_LABEL_KEY[v]) }))}
          selected={new Set(regionFilter)}
          onChange={(s) => setRegionFilter(Array.from(s))}
        />
        <Toggle label={t("biens.meuble")} checked={meuble} onChange={setMeuble} />
        <Toggle label={t("v2.catalogue.companyLabel")} checked={company} onChange={setCompany} />
        <PostalCodeFilter codes={postalCodes} onChange={setPostalCodes} />
        <button
          type="button"
          onClick={resetSelectionFilters}
          className="flex items-center gap-1.5 px-2.5 py-2 text-xs text-text-muted hover:text-accent cursor-pointer transition-colors"
          title={t("v2.catalogue.resetTitle")}
        >
          <RotateCcw size={13} />
          <span className="hidden sm:inline">{t("common.resetFilters")}</span>
        </button>
      </div>

      {/* Recherche libre — APPLIQUÉE AVANT pagination (cherche dans tous
          les biens, pas juste la page courante). */}
      <div className="mb-3 relative max-w-md">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("v2.catalogue.searchPlaceholder")}
          className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md border border-border bg-surface outline-none"
        />
      </div>

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(b) => b.id}
        onRowClick={(b) => navigate(`/v2/biens/${b.id}`)}
        searchable={false}
        sort={sort}
        onSortChange={saveSort}
        isLoading={isLoading}
        minWidth={tableMinWidth}
        fillViewport
        emptyLabel={search ? t("v2.catalogue.emptySearch") : t("v2.catalogue.emptyFilter")}
      />

      {/* Pagination */}
      {filtered.length > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-text-muted">
            {t("v2.catalogue.paginationRange", { start: startIdx, end: endIdx, total: filtered.length })}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={safePage === 0}
              className="p-1.5 rounded-md border border-border hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="text-xs px-3 py-1.5 bg-surface border border-border rounded-md font-medium">
              {t("v2.catalogue.pageOf", { page: safePage + 1, total: pageCount })}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              disabled={safePage >= pageCount - 1}
              className="p-1.5 rounded-md border border-border hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {canConfigureColumns && (
        <ColumnPickerModal<CatalogueColumnKey>
          isOpen={columnPickerOpen}
          initialSelection={visibleColumns}
          registry={CATALOGUE_COLUMNS}
          defaults={defaultVisibleKeys(CATALOGUE_COLUMNS)}
          onClose={() => setColumnPickerOpen(false)}
          onSave={(sel) => {
            saveColumns(sel);
            setColumnPickerOpen(false);
          }}
        />
      )}
    </div>
  );
}
